import {
  CheckboxField,
  InputField,
  PhoneNoField,
  TextareaField,
} from '@/components/FormElements';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import Typography from '@/components/ui/typography';
import { MobileNoValidator } from '@/lib/customValidators';
import { cn } from '@/lib/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, router } from '@inertiajs/react';
import { SendHorizontal } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

/*
 * PhoneNoField's country picker is a Radix Select, which keeps its value in a
 * hidden native <select name="country_code"> rather than in react-hook-form —
 * so it has to be read out of the DOM.
 *
 * Scoped to the owning form rather than the document: the original lookup took
 * getElementsByName('country_code')[0], which reads the wrong picker as soon as
 * a page carries two forms. Now that this component is shared between the
 * contact page and the home page's closing band, that stops being hypothetical.
 */
const readCountryCode = (form: HTMLFormElement | null) => {
  const select =
    form?.querySelector<HTMLSelectElement>('[name="country_code"]') ??
    (document.getElementsByName('country_code')[0] as
      HTMLSelectElement | undefined);

  return select?.value ?? 'IN';
};

/*
 * Built as a factory rather than a module constant so the mobile-number test
 * can read the country picker at validation time. Passing the form through
 * useForm's `context` would not work — that value is snapshotted on first
 * render, when the ref is still null.
 */
const makeInquirySchema = (getForm: () => HTMLFormElement | null) =>
  yup.object({
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    mobile_no: yup
      .string()
      .required('Mobile number is required')
      .test(
        'mobile-no-validator',
        'Invalid mobile number',
        (value, context) => {
          const error = MobileNoValidator(value, readCountryCode(getForm()));
          return error ? context.createError({ message: error }) : true;
        }
      ),
    email: yup
      .string()
      .required('Email is required')
      .max(255, 'Email must be less than 255 characters long')
      .email('Invalid email'),
    message: yup
      .string()
      .trim()
      .required('Message is required')
      .min(20, 'Message must be at least 20 characters long')
      .max(255, 'Message must not exceed 255 characters'),
    user_consent: yup
      .boolean()
      .oneOf([true], 'You must agree to the terms and conditions')
      .default(false),
  });

type InquiryFormData = yup.InferType<ReturnType<typeof makeInquirySchema>>;

/*
 * The enquiry form, lifted out of Pages/Contact.tsx so the home page's closing
 * band can post to the same /inquire route under the same rules.
 *
 * Every column on the `inquiries` table is NOT NULL, so this cannot be trimmed
 * to a shorter "quick enquiry" without a migration — the field count is the
 * schema's requirement, not a design choice.
 */
const InquiryForm = ({ className }: { className?: string }) => {
  const formRef = useRef<HTMLFormElement>(null);

  const schema = useMemo(() => makeInquirySchema(() => formRef.current), []);

  const form = useForm<InquiryFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      mobile_no: '',
      email: '',
      message: '',
      user_consent: false,
    },
  });

  // destructure form state to subscribe to proxy form state
  const { isSubmitting } = form.formState;

  const onValidData = async (data: InquiryFormData) => {
    const countryCode = readCountryCode(formRef.current);

    await new Promise<void>((resolve) =>
      router.post(
        route('inquire'),
        {
          ...data,
          country_code: countryCode,
        },
        {
          onError: (errors) => {
            for (const field in errors) {
              const error = errors[field];

              // If the field is in the data object, set the error on the form
              // Otherwise, show the error as a toast
              if (Object.prototype.hasOwnProperty.call(data, field)) {
                form.setError(field as keyof typeof data, {
                  type: 'server',
                  message: error,
                });
              } else {
                toast.error(error);
              }
            }
          },
          onSuccess: ({ props }) => {
            toast.success('Inquiry sent', {
              description: props.status as string,
            });
            form.reset();
          },
          onFinish: () => resolve(),
        }
      )
    );
  };

  return (
    <Form {...form}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(onValidData)}
        className={cn('space-y-6', className)}
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <InputField
            control={form.control}
            name="first_name"
            label="First Name"
            autoComplete="given-name"
          />

          <InputField
            control={form.control}
            name="last_name"
            label="Last Name"
            autoComplete="family-name"
          />

          <PhoneNoField
            control={form.control}
            name="mobile_no"
            label="Mobile No"
          />

          <InputField
            control={form.control}
            type="email"
            name="email"
            label="Email"
            autoComplete="email"
          />

          <div className="sm:col-span-2">
            <TextareaField
              control={form.control}
              name="message"
              label="Message"
              placeholder="Type your message here..."
              resizable
              rows={5}
            />
          </div>
        </div>

        <CheckboxField
          name="user_consent"
          label="I agree to the terms and conditions"
          description={() => (
            <>
              By checking this box, you consent to receive marketing emails and
              agree to the use of your personal data in accordance with our{' '}
              <Link
                tabIndex={-1}
                href={route('privacy-policy')}
                className="text-primary underline-offset-4 hover:underline"
              >
                Privacy Policy
              </Link>
            </>
          )}
          control={form.control}
        />

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-fit"
          >
            Send enquiry <SendHorizontal size={16} className="ml-2" />
          </Button>

          <Typography.Small>
            Your message goes straight to our support team.
          </Typography.Small>
        </div>
      </form>
    </Form>
  );
};

export default InquiryForm;
